import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Container,
} from '@mui/material';
import {
  Lock as LockIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

const Login: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const result = await login(username, password);
      setLoading(false);
      if (result.success) {
        // Show success message if provided
        if (result.message) {
          // Store message in sessionStorage to show after navigation
          sessionStorage.setItem('loginMessage', result.message);
        }
        
        // Redirect to the original URL or default to viewer
        const redirectUrl = sessionStorage.getItem('redirectAfterLogin') || localStorage.getItem('redirectAfterLogin');
        console.log('🔗 Redirect URL found (sessionStorage):', sessionStorage.getItem('redirectAfterLogin'));
        console.log('🔗 Redirect URL found (localStorage):', localStorage.getItem('redirectAfterLogin'));
        console.log('🔗 Final redirect URL:', redirectUrl);
        console.log('🔗 Is document-viewer route?', redirectUrl?.includes('/document-viewer'));
        console.log('🔗 Has XML param?', redirectUrl?.includes('xml='));
        console.log('🔗 Has docId param?', redirectUrl?.includes('docId='));
        console.log('🔗 Has get param?', redirectUrl?.includes('get&'));
        console.log('🔗 Has pVersion param?', redirectUrl?.includes('pVersion='));
        console.log('🔗 URL length:', redirectUrl?.length);
        console.log('🔗 URL preview:', redirectUrl?.substring(0, 100) + '...');
        console.log('🔗 All sessionStorage keys:', Object.keys(sessionStorage));
        console.log('🔗 Current location:', window.location.href);
        
        if (redirectUrl) {
          sessionStorage.removeItem('redirectAfterLogin');
          localStorage.removeItem('redirectAfterLogin');
          console.log('🚀 Redirecting to:', redirectUrl);
          console.log('🚀 Navigation starting...');
          
          // Special handling for document-viewer routes
          if (redirectUrl.includes('/document-viewer')) {
            console.log('🚀 Special handling for document-viewer route');
            console.log('🚀 Redirect URL contains XML?', redirectUrl.includes('xml='));
            console.log('🚀 Redirect URL contains normalized params?', redirectUrl.includes('get&'));
            
            if (redirectUrl.includes('xml=')) {
              console.log('🚀 XML route - forcing page reload');
              // Force a page reload to ensure the DocumentViewer processes the XML correctly
              window.location.href = redirectUrl;
            } else {
              console.log('🚀 Normalized route - using navigation');
              // Use normal navigation for normalized URLs
              navigate(redirectUrl, { replace: true });
            }
          } else {
            console.log('🚀 Normal navigation to:', redirectUrl);
            navigate(redirectUrl, { replace: true });
          }
        } else {
          console.log('🚀 No redirect URL, going to default /viewer');
          console.log('🚀 Available sessionStorage items:');
          for (let i = 0; i < sessionStorage.length; i++) {
            const key = sessionStorage.key(i);
            if (key) {
              console.log(`   ${key}: ${sessionStorage.getItem(key)}`);
            }
          }
          console.log('🚀 Available localStorage items:');
          for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key) {
              console.log(`   ${key}: ${localStorage.getItem(key)}`);
            }
          }
          navigate('/viewer', { replace: true });
        }
      } else {
        setError(result.message || 'Invalid username or password');
      }
    } catch (err) {
      setError('An error occurred during login. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            width: '100%',
            maxWidth: 400,
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <LockIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
              DataUnify Intelligent Viewer
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Welcome to DU-IV
            </Typography>
          </Box>

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              margin="normal"
              required
              InputProps={{
                startAdornment: <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
              disabled={loading}
            />
            <TextField
              fullWidth
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              required
              InputProps={{
                startAdornment: <LockIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
              disabled={loading}
            />

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading || !username || !password}
              sx={{ mt: 3, mb: 2 }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Demo Credentials:
            </Typography>
            <Typography variant="body2" color="primary" sx={{ fontWeight: 'bold' }}>
              Username: user | Password: pass
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Login; 